import React, { useMemo } from 'react';
import Image, { type StaticImageData } from 'next/image';
import { useTranslation } from 'next-i18next';
import { useRouter } from 'next/router';

import footerNavigation from '@constants/footerNavigation.json';
import {
  StyledFooter,
  FooterTop,
  FooterBottom,
  FooterContainer,
  LogoContainer,
  List,
  ListItem,
  ListItemLink,
  EmailAnchor,
  ListItemHeader,
  Buttons,
  PhoneNumber,
  FooterBottomText,
  FooterPoweredBy,
} from '@components/Footer/style';

import Logo from '@assets/images/logo-lodz.svg';

type itemFooterType = {
  id: number;
  title: string;
  link?: string;
};

type contactItemType = itemFooterType & { email?: string };

type ButtonType = {
  id: number;
  link: string;
  image: string;
};

type FooterLocaleData = {
  offices: itemFooterType[];
  contact: contactItemType[];
  buttons: ButtonType[];
};

// footerNavigation.json is keyed by locale; type the access so the destructured
// arrays are typed instead of resolving to `any`. `pl` is declared explicitly so
// it stays a guaranteed fallback.
const footerNavigationByLocale = footerNavigation as { pl: FooterLocaleData } & Record<string, FooterLocaleData>;

type FooterProps = {
  items: FooterNavigation;
};

function Footer({ items }: FooterProps) {
  const { t } = useTranslation('navbar');
  const { locale } = useRouter();
  const { offices, contact, buttons } = useMemo(
    () => footerNavigationByLocale[locale ?? 'pl'] ?? footerNavigationByLocale.pl,
    [locale],
  );
  return (
    <StyledFooter>
      <FooterTop>
        <FooterContainer>
          <LogoContainer>
            <Image src={Logo as StaticImageData} alt="" />
          </LogoContainer>
          <List>
            {items.map((page: itemFooterType) => (
              <ListItemLink href={page.link ?? ''} key={page.id}>
                {page.title}
              </ListItemLink>
            ))}
          </List>
          <List className="offices">
            {offices.map((office: itemFooterType) => (
              <ListItemLink href={office.link ?? ''} key={office.id}>
                {office.title}
              </ListItemLink>
            ))}
          </List>
          <List>
            <ListItemHeader>{t('contact')}</ListItemHeader>
            {contact.map((item: contactItemType) => (
              <ListItem key={item.id}>
                {item.email ? (
                  <EmailAnchor href={`mailto:${item.email}`}>{item.title}</EmailAnchor>
                ) : (
                  <PhoneNumber>{item.title}</PhoneNumber>
                )}
              </ListItem>
            ))}
            <Buttons>
              {buttons.map((button: ButtonType) => (
                <a href={button.link} target="_blank" rel="noreferrer" key={button.id}>
                  <img src={button.image} alt="" />
                </a>
              ))}
            </Buttons>
          </List>
        </FooterContainer>
      </FooterTop>
      <FooterBottom>
        <FooterBottomText>
          Wszelkie prawa zastrzeżone | All Rights Reserved © Urząd Miasta Łodzi 2008-{new Date().getFullYear()}
          <FooterPoweredBy>
            powered by{' '}
            <a href="https://goodylabs.com" target="_blank" rel="noreferrer">
              <img src="/images/goodylabs.png" alt="goodylabs.com" />
              goodylabs.com
            </a>
          </FooterPoweredBy>
        </FooterBottomText>
      </FooterBottom>
    </StyledFooter>
  );
}

export default Footer;
